/**
 * @file 碰 / 杠 / 胡 决策 —— `design/gdd/06-ai-strategy.md` §5。
 *
 * 血战到底中最微妙的决策是**杠**：杠分即时到账，但流局未听会被呼叫转移全额吐回、
 * 巴杠可能被抢杠胡、4 张锁死还会退向听。困难档用完整的 `gangValue` 权衡（§5.3）。
 *
 * 纯函数、平台无关。`shouldPeng` 的简单档分支需要随机数 —— 为保持确定性流，
 * 它接收 `RngState` 并返回推进后的状态（与 `prng.ts` 的「取值即推进」一致）。
 */

import type { RngState, Suit, TileKind } from '../types';
import { keyOfKind, kindFromKindIndex, suitToIndex } from '../tiles';
import { nextRandom } from '../prng';
import type { AIDifficulty } from '../config';
import type { GangCandidate, AIObservation, AIOpponentInfo, TileCounts, TileKey } from './types';
import { GANG_SCORE, GEN_FAN_VALUE } from './params';
import { copyCounts, shantenQiDui, shantenSichuan, shantenStandard } from './shanten';
import { threatLevel } from './discard';

/* ─────────────────────────── §5.1 胡的决策 ─────────────────────────── */

/**
 * 是否胡牌（§5.1）。所有难度：能胡就胡。
 *
 * 血战到底中「见胡就胡」接近最优：胡牌即刻锁定收益并永久退出点炮风险；
 * 放弃小胡搏大胡在 `fanCap = 4` 下期望收益远不足以补偿风险。
 *
 * ⚠ 预留 `allowSkipSmallHu`（默认 `false`）供后续调优实验，当前恒返回 `true`。
 */
export function shouldHu(_obs: AIObservation, _difficulty: AIDifficulty): boolean {
  return true;
}

/* ─────────────────────────── 番型倾向判定 ─────────────────────────── */

/**
 * 是否七对倾向（§5.2）：`meldCount === 0 && shantenQiDui <= shantenStandard`。
 * 碰牌会永久断绝七对可能（`meldCount > 0`），困难 AI 必须检查这一点。
 *
 * @param queSuit 定缺；缺门牌剔除后比较（与 `shantenSichuan` 口径一致）
 */
export function isQiDuiOriented(counts: TileCounts, meldCount: number, queSuit: Suit | null): boolean {
  if (meldCount > 0) return false;
  const c = copyCounts(counts);
  if (queSuit !== null) {
    const base = suitToIndex(queSuit) * 9;
    for (let r = 0; r < 9; r++) c[base + r] = 0;
  }
  return shantenQiDui(c, meldCount) <= shantenStandard(c, meldCount);
}

/**
 * 是否对对胡倾向（§5.2 平向听时的碰牌条件）。
 *
 * `06` 未给出定义，本实现采用工程化启发式：非缺门花色中对子/刻子（count ≥ 2）
 * 的牌种数 ≥ 4。可供设计侧复核（开放问题）。
 */
export function isDuiDuiOriented(counts: TileCounts, queSuit: Suit | null): boolean {
  let blocks = 0;
  for (let k = 0; k < 27; k++) {
    if (queSuit !== null && Math.floor(k / 9) === suitToIndex(queSuit)) continue;
    if ((counts[k] ?? 0) >= 2) blocks++;
  }
  return blocks >= 4;
}

/* ─────────────────────────── §5.2 碰的决策 ─────────────────────────── */

/**
 * 是否碰（§5.2）。
 *
 * - 简单档：`random() < 0.85`（新手通病，几乎见碰就碰）；
 * - 普通/困难档：比较碰前碰后的向听数 ——
 *   退向听绝不碰；进向听才碰（困难档额外检查不破坏七对倾向）；
 *   平向听只有困难档对对胡/清一色倾向时才碰。
 *
 * @param rng 简单档分支消费一个随机数；返回推进后的状态（非简单档不消费，原样返回）
 */
export function shouldPeng(
  obs: AIObservation,
  tileKey: TileKey,
  difficulty: AIDifficulty,
  rng: RngState,
): { readonly decision: boolean; readonly rng: RngState } {
  if (difficulty === 'EASY') {
    const r = nextRandom(rng);
    return { decision: r.value < 0.85, rng: r.state };
  }

  const counts = copyCounts(obs.hand);
  const meldCount = obs.melds.length;
  const before = shantenSichuan(counts, meldCount, obs.queSuit);

  counts[tileKey] = (counts[tileKey] ?? 0) - 2;
  const after = shantenSichuan(counts, meldCount + 1, obs.queSuit);

  if (after > before) return { decision: false, rng }; // 退向听，绝不碰
  if (after < before) {
    // 进向听；但困难档检查是否破坏七对倾向
    if (difficulty === 'HARD' && isQiDuiOriented(obs.hand, meldCount, obs.queSuit)) {
      return { decision: false, rng };
    }
    return { decision: true, rng };
  }
  // 平向听：只有对对胡/清一色倾向时才碰（普通档恒 false）
  return { decision: difficulty === 'HARD' && isDuiDuiOriented(obs.hand, obs.queSuit), rng };
}

/* ─────────────────────────── §5.3 杠的决策 ─────────────────────────── */

/**
 * 流局概率的简化模型（§5.3）：
 * `pDraw ≈ clamp((remaining / 55) * (0.55 - 0.15 * wonCount), 0, 1)`
 * （剩余牌越多、已胡玩家越少 → 越可能流局）。
 */
export function estimateDrawProbability(
  remaining: number,
  opponents: readonly AIOpponentInfo[],
): number {
  let wonCount = 0;
  for (const o of opponents) if (o.status === 'WON') wonCount++;
  return clamp01((remaining / 55) * (0.55 - 0.15 * wonCount));
}

/**
 * 巴杠被抢杠胡的风险估计（§5.3 的 `estimateQiangGangRisk`）。
 *
 * `06` 未给出定义，本实现采用工程化启发式：对每个仍在场、缺门不包含该牌、
 * 牌河没打过该牌、且该牌未绝张的对手，累加其威胁度 × 0.10，封顶 1.0。
 * 可供设计侧复核（开放问题）。
 */
export function estimateQiangGangRisk(
  obs: AIObservation,
  tileKey: TileKey,
  seen: Uint8Array,
): number {
  let risk = 0;
  for (const o of obs.opponents) {
    if (o.status === 'WON') continue;
    if (o.queSuit !== null && Math.floor(tileKey / 9) === suitToIndex(o.queSuit)) continue;
    if (o.discards.includes(tileKey)) continue;
    if ((seen[tileKey] ?? 0) >= 4) continue; // 已绝张，无人能抢
    risk += threatLevel(o, obs) * 0.1;
  }
  return clamp01(risk);
}

/**
 * 杠的估值（§5.3）。血战到底中杠是一把双刃剑：
 *
 * - 收益：杠分即时到账（明 1 / 暗 2 / 巴 1）+ 根的期望价值（≈1.5）；
 * - 风险：退向听重罚（每向听 -4.0）；困难档再扣呼叫转移风险
 *   （流局且自己没听时杠分全额吐回）与巴杠的抢杠胡风险。
 *
 * 杠后手牌/副露的变化：
 * - `AN_GANG`：手牌 -4，副露 +1；
 * - `MING_GANG`：手牌 -3，副露 +1；
 * - `BA_GANG`：手牌 -1，副露数不变（碰 → 补杠）。
 *
 * @param seen 困难档估计抢杠胡风险需要；普通/简单档传空表即可
 */
export function gangValue(
  obs: AIObservation,
  gang: GangCandidate,
  difficulty: AIDifficulty,
  seen: Uint8Array,
): number {
  if (difficulty === 'EASY') return 1.0; // 简单 AI 见杠就杠

  const counts = copyCounts(obs.hand);
  const meldCount = obs.melds.length;
  const k = keyOfKind(gang.kind);
  const before = shantenSichuan(counts, meldCount, obs.queSuit);

  let after: number;
  let meldCountAfter = meldCount;
  switch (gang.gangType) {
    case 'AN_GANG':
      counts[k] = (counts[k] ?? 0) - 4;
      meldCountAfter = meldCount + 1;
      break;
    case 'MING_GANG':
      counts[k] = (counts[k] ?? 0) - 3;
      meldCountAfter = meldCount + 1;
      break;
    case 'BA_GANG':
      counts[k] = (counts[k] ?? 0) - 1; // 碰 → 补杠，副露数不变
      break;
  }
  after = shantenSichuan(counts, meldCountAfter, obs.queSuit);
  const shantenDelta = after - before; // >0 表示退向听

  const gangScore = GANG_SCORE[gang.gangType] ?? 1;
  let value = gangScore + GEN_FAN_VALUE - shantenDelta * 4.0;

  if (difficulty === 'HARD') {
    // ★ 呼叫转移风险：估算流局且自己没听的概率
    const pDraw = estimateDrawProbability(obs.remaining, obs.opponents);
    const pNotTing = clamp01(before * 0.28); // 向听越远，越可能听不上
    value -= pDraw * pNotTing * gangScore * 1.4;

    // 巴杠的抢杠胡风险
    if (gang.gangType === 'BA_GANG') {
      value -= estimateQiangGangRisk(obs, k, seen) * 6.0;
    }
  }

  return value;
}

/**
 * 是否杠（§5.3）：`gangValue(...) > 0`。
 *
 * 防御性守卫：牌墙已空时杠了无牌可补（`forbidGangWhenWallEmpty`），直接拒绝
 * （引擎也会拒，这里是让 AI 不浪费一次动作）。
 */
export function shouldGang(
  obs: AIObservation,
  gang: GangCandidate,
  difficulty: AIDifficulty,
  seen: Uint8Array,
): boolean {
  if (obs.remaining <= 0) return false;
  return gangValue(obs, gang, difficulty, seen) > 0;
}

/* ─────────────────────────── §7 主循环需要的杠枚举 ─────────────────────────── */

/**
 * 自己回合可发起的杠候选（`SELF_ACTION`）：
 * - 暗杠：手牌 4 张；
 * - 巴杠：已有碰的刻子 + 手牌中第 4 张。
 *
 * （明杠需要他家打出那张牌，属于 `RESPONSE_WINDOW`，由 `canMingGang` 单独判定。）
 */
export function listSelfGangs(obs: AIObservation): readonly GangCandidate[] {
  const out: GangCandidate[] = [];
  const counts = obs.hand;
  for (let k = 0; k < 27; k++) {
    if ((counts[k] ?? 0) >= 4) {
      out.push({ kind: kindFromKindIndex(k), gangType: 'AN_GANG' });
    }
  }
  const pengKinds = new Set<TileKind>();
  for (const m of obs.melds) {
    if (m.type === 'peng' && m.kind !== null) pengKinds.add(m.kind);
  }
  for (const kind of pengKinds) {
    const k = keyOfKind(kind);
    if ((counts[k] ?? 0) >= 1) {
      out.push({ kind, gangType: 'BA_GANG' });
    }
  }
  return out;
}

/** 响应窗口能否明杠：手牌中该牌 ≥ 3 张。 */
export function canMingGang(obs: AIObservation, tileKey: TileKey): boolean {
  return (obs.hand[tileKey] ?? 0) >= 3;
}

/** 响应窗口能否碰：手牌中该牌 ≥ 2 张。 */
export function canPeng(obs: AIObservation, tileKey: TileKey): boolean {
  return (obs.hand[tileKey] ?? 0) >= 2;
}

/** 钳制到 [0, 1]。 */
function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/** 导出钳制函数供内部与测试使用。 */
export { clamp01 };
